import React, { useEffect } from 'react';
import { Card, Row, Col, Table, Tag, Space, Spin, DatePicker, Empty, message } from 'antd';
import {
  UserOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  TeamOutlined,
  ShopOutlined,
  TruckOutlined,
  RiseOutlined,
  WarningOutlined,
  StopOutlined,
  ClockCircleOutlined,
  CarOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Area, Pie, Column } from '@ant-design/charts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const fmtInt = (v) => Number(v || 0).toLocaleString('en-IN');
const fmtMoney = (v) =>
  `Rs. ${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Status → dot/tag color for the live activity feed
const FEED_STATUS_COLOR = {
  completed: '#16a34a',
  delivered: '#22c55e',
  pending: '#f59e0b',
  accepted: '#3b82f6',
  confirmed: '#3b82f6',
  packaging: '#8b5cf6',
  preparing: '#8b5cf6',
  ready: '#14b8a6',
  out_for_delivery: '#6366f1',
  on_the_way: '#6366f1',
  canceled: '#ef4444',
  cancelled: '#ef4444',
  refunded: '#b91c1c',
};

const StatTile = ({ icon, label, value }) => (
  <div className="kpi">
    <div className="kpi-icon">{icon}</div>
    <div className="kpi-body">
      <span className="kpi-value">{value}</span>
      <span className="kpi-label">{label}</span>
    </div>
  </div>
);

// Order status: reserved semantic colors, fixed order, always shipped with labels + legend.
const STATUS_META = [
  { key: 'pendingOrders', name: 'Pending', color: '#f59e0b' },
  { key: 'confirmedOrders', name: 'Accepted', color: '#3b82f6' },
  { key: 'preparingOrders', name: 'Packaging', color: '#8b5cf6' },
  { key: 'readyOrders', name: 'Ready', color: '#14b8a6' },
  { key: 'onTheWayOrders', name: 'Out for Delivery', color: '#6366f1' },
  { key: 'deliveredOrders', name: 'Delivered', color: '#22c55e' },
  { key: 'completedOrders', name: 'Completed', color: '#15803d' },
  { key: 'cancelledOrders', name: 'Cancelled', color: '#ef4444' },
  { key: 'refundedOrders', name: 'Refunded', color: '#b91c1c' },
  { key: 'partiallyRefundedOrders', name: 'Partially Refunded', color: '#ec4899' },
];

// Categorical palette — validated CVD-safe in light mode; assigned in fixed order.
const CATEGORY_COLORS = ['#6d28d9', '#2e9bd6', '#0d9488', '#d97706', '#db2777', '#16a34a'];

const Dashboard = () => {
  const navigate = useNavigate();

  // Fetch notifications with proper configuration
  useQuery(
    'admin-notifications',
    async () => {
      try {
        const response = await api.get('/admin/admin-notifications');
        const { data } = response.data;
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Failed to load notifications:', error);
        return [];
      }
    },
    {
      refetchInterval: false, // Disable automatic refetch
      refetchOnWindowFocus: false, // Disable refetch on focus
      staleTime: 10 * 60 * 1000, // 10 minutes cache
      retry: 1, // Only retry once
    },
  );

  // Fetch system settings
  useQuery(
    'admin-settings',
    async () => {
      try {
        const response = await api.get('/admin/settings');
        return response.data?.data || {};
      } catch (error) {
        console.error('Failed to load settings:', error);
        return {};
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Remove auto-refresh effect to prevent continuous API calls
  // Manual refresh can be triggered by user action via refreshDashboard function

  // Fetch dashboard statistics
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery(
    'dashboard-stats',
    async () => {
      try {
        const response = await api.get('/admin/dashboard-stats');
        const data = response.data?.data || response.data || {};

        // Return comprehensive dashboard data from backend
        return {
          // Basic counts
          totalUsers: data.total_users || 0,
          totalOrders: data.total_orders || 0,
          totalProducts: data.total_products || 0,
          totalProviders: data.total_providers || 0,
          totalCustomers: data.total_customers || 0,
          totalDeliveryPersonnel: data.total_delivery_personnel || 0,
          activeProducts: data.active_products || 0,

          // Revenue & profit analytics
          totalRevenue: data.total_revenue || 0,
          totalProfit: data.total_profit || 0,
          profitMargin: data.profit_margin || 0,
          monthlyRevenue: data.monthly_revenue || [],
          revenueGrowth: data.revenue_growth || 0,
          dailyRevenue: data.daily_revenue || [],
          averageOrderValue: data.average_order_value || 0,

          // Order statistics
          pendingOrders: data.order_stats?.pending || 0,
          confirmedOrders: data.order_stats?.confirmed || 0,
          preparingOrders: data.order_stats?.preparing || 0,
          readyOrders: data.order_stats?.ready || 0,
          onTheWayOrders: data.order_stats?.on_the_way || 0,
          deliveredOrders: data.order_stats?.delivered || 0,
          completedOrders: data.order_stats?.completed || 0,
          cancelledOrders: data.order_stats?.cancelled || 0,
          refundedOrders: data.order_stats?.refunded || 0,
          partiallyRefundedOrders: data.order_stats?.partially_refunded || 0,

          // Top performers
          topProducts: data.top_products || [],
          topCategories: data.top_categories || [],
          topProviders: data.top_providers || [],

          // Financial analytics
          totalRefunded: data.revenue_analytics?.total_refunded || 0,
          monthlyRefunded: data.revenue_analytics?.monthly_refunded || [],
          dailyRefunded: data.revenue_analytics?.daily_refunded || [],
          revenueByPaymentMethod: data.revenue_analytics?.revenue_by_payment_method || [],
          revenueByOrderType: data.revenue_analytics?.revenue_by_order_type || [],
          monthlyTrend: data.revenue_analytics?.monthly_trend || [],

          // Recent activities
          recentActivities: data.recent_activities || [],

          // System health
          systemHealth: data.systemHealth || {
            database: 'unknown',
            api: 'unknown',
            cache: 'unknown',
            server: 'unknown',
            last_updated: new Date().toISOString(),
          },
          unreadMessages: 0,
          pendingNotifications: 0,
        };
      } catch (error) {
        console.error('Failed to load dashboard stats:', error);
        // Return fallback data on error
        return {
          totalUsers: 0,
          totalOrders: 0,
          totalProducts: 0,
          totalProviders: 0,
          totalCustomers: 0,
          totalDeliveryPersonnel: 0,
          totalRevenue: 0,
          totalProfit: 0,
          profitMargin: 0,
          monthlyRevenue: [],
          revenueGrowth: 0,
          dailyRevenue: [],
          averageOrderValue: 0,
          pendingOrders: 0,
          confirmedOrders: 0,
          preparingOrders: 0,
          readyOrders: 0,
          onTheWayOrders: 0,
          deliveredOrders: 0,
          completedOrders: 0,
          cancelledOrders: 0,
          totalRefunded: 0,
          monthlyRefunded: [],
          dailyRefunded: [],
          revenueByPaymentMethod: [],
          revenueByOrderType: [],
          monthlyTrend: [],
          topProducts: [],
          topCategories: [],
          topProviders: [],
          recentActivities: [],
          systemHealth: {
            database: 'unknown',
            api: 'unknown',
            cache: 'unknown',
            server: 'unknown',
            last_updated: new Date().toISOString(),
          },
          unreadMessages: 0,
          pendingNotifications: 0,
        };
      }
    },
    {
      retry: (failureCount, error) => {
        // Only retry on network errors, not on rate limiting (429) or server errors (5xx)
        if (error.response?.status === 429) return false;
        if (error.response?.status >= 500) return false;
        return failureCount < 2; // Max 2 retries
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchInterval: 60000, // Refresh once a minute so the live feed/alerts stay current
      staleTime: 60 * 1000,
    },
  );

  // Inventory health for critical alerts (low / out of stock)
  const { data: inventoryStats = {} } = useQuery(
    'inventory-stats',
    async () => {
      try {
        const response = await api.get('/admin/inventory/stats');
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    { refetchInterval: 60000, refetchOnWindowFocus: false, staleTime: 60 * 1000, retry: 1 },
  );

  // Named low-stock products (lowest stock first) to surface in the alert list
  const { data: lowStockProducts = [] } = useQuery(
    'low-stock-products',
    async () => {
      try {
        const response = await api.get('/admin/inventory/products', {
          params: {
            stock_filter: 'low_stock',
            per_page: 5,
            sort_field: 'stock_quantity',
            sort_order: 'asc',
          },
        });
        const payload = response.data?.data;
        return Array.isArray(payload) ? payload : payload?.data || [];
      } catch (error) {
        return [];
      }
    },
    { refetchInterval: 60000, refetchOnWindowFocus: false, staleTime: 60 * 1000, retry: 1 },
  );

  // Fetch recent orders
  const {
    data: recentOrders = [],
    isLoading: ordersLoading,
    error: ordersError,
  } = useQuery(
    'recent-orders',
    async () => {
      try {
        const response = await api.get('/admin/orders?limit=10&sort=created_at,desc');
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Recent orders error:', error);
        // Return empty array if API fails
        return [];
      }
    },
    {
      retry: (failureCount, error) => {
        if (error.response?.status === 429) return false;
        if (error.response?.status >= 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
      refetchOnWindowFocus: false,
      refetchInterval: 60000, // Keep the live activity feed current
      staleTime: 60 * 1000,
      onError: (_error) => {
        console.error('Failed to load recent orders:', _error);
      },
    },
  );

  // Show error messages when API calls fail
  useEffect(() => {
    if (statsError) {
      if (statsError.response?.status === 429) {
        message.error('Too many requests. Please wait a moment and refresh.');
      } else if (statsError.response?.status >= 500) {
        message.error('Server error. Please try again later.');
      } else {
        message.error('Failed to load dashboard statistics.');
      }
    }

    if (ordersError) {
      if (ordersError.response?.status === 429) {
        message.error('Too many requests. Please wait a moment and refresh.');
      } else if (ordersError.response?.status >= 500) {
        message.error('Server error. Please try again later.');
      } else {
        message.error('Failed to load recent orders.');
      }
    }
  }, [statsError, ordersError]);

  const orderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'order_number',
      key: 'order_number',
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer) => customer?.name || customer?.full_name || 'N/A',
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'order_status',
      key: 'order_status',
      render: (status) => {
        const colorMap = {
          completed: 'green',
          delivered: 'green',
          pending: 'orange',
          accepted: 'blue',
          packaging: 'purple',
          out_for_delivery: 'cyan',
          canceled: 'red',
          refunded: 'red',
        };
        return (
          <Tag color={colorMap[status] || 'default'}>{status?.replace('_', ' ').toUpperCase()}</Tag>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  // Revenue chart data
  const revenueData = (stats?.monthlyTrend || []).map((item) => ({
    month: item.month,
    revenue: parseFloat(item.revenue || 0),
  }));

  // Order status distribution — semantic colors, only statuses that actually have orders.
  const activeStatuses = STATUS_META.filter((s) => (stats?.[s.key] || 0) > 0);
  const orderStatusData = activeStatuses.map((s) => ({ name: s.name, value: stats?.[s.key] || 0 }));
  const orderStatusColors = activeStatuses.map((s) => s.color);
  const totalStatusOrders = orderStatusData.reduce((sum, d) => sum + d.value, 0);

  // Top products data
  const topProductsData =
    stats?.topProducts?.slice(0, 5).map((product) => ({
      name: product.name || 'Unknown Product',
      sales: product.order_items_count || 0,
    })) || [];

  // Category distribution data — top 6 categories by product count.
  const categoryData =
    stats?.topCategories?.slice(0, 6).map((cat) => ({
      category: cat.name || 'Uncategorized',
      count: cat.product_count || 0,
    })) || [];
  const totalCategoryProducts = categoryData.reduce((sum, d) => sum + d.count, 0);

  // Critical alerts the admin should act on (hidden when count is 0)
  const lowStockNames = (Array.isArray(lowStockProducts) ? lowStockProducts : [])
    .map((p) => p.name)
    .filter(Boolean)
    .slice(0, 2)
    .join(', ');
  const alerts = [
    {
      key: 'low-stock',
      icon: <WarningOutlined />,
      label: 'Low on stock',
      hint: lowStockNames,
      count: inventoryStats?.low_stock_count || 0,
      to: '/inventory',
    },
    {
      key: 'out-of-stock',
      icon: <StopOutlined />,
      label: 'Out of stock',
      count: inventoryStats?.out_of_stock_count || 0,
      to: '/inventory',
    },
    {
      key: 'pending',
      icon: <ClockCircleOutlined />,
      label: 'Orders awaiting action',
      count: stats?.pendingOrders || 0,
      to: '/orders',
    },
    {
      key: 'delivery',
      icon: <CarOutlined />,
      label: 'Out for delivery',
      count: stats?.onTheWayOrders || 0,
      to: '/live-tracking',
    },
  ].filter((a) => a.count > 0);

  // Live activity feed — recent orders with who / what / which provider.
  const activityFeed = (Array.isArray(recentOrders) ? recentOrders : []).slice(0, 12).map((o) => {
    const items = Array.isArray(o.items) ? o.items : [];
    const productNames = items
      .map((it) => it.product?.name)
      .filter(Boolean);
    const what = productNames.length
      ? `${productNames[0]}${productNames.length > 1 ? ` +${productNames.length - 1} more` : ''}`
      : `${items.length || 1} item${items.length > 1 ? 's' : ''}`;
    const provider =
      o.provider?.business_name ||
      o.provider?.full_name ||
      (items[0]?.product || items[0]?.product)?.provider?.business_name ||
      (items[0]?.product || items[0]?.product)?.provider?.full_name ||
      null;
    return {
      id: o.id,
      customer: o.customer?.full_name || o.customer?.name || 'A customer',
      what,
      provider,
      amount: o.total_amount != null ? fmtMoney(o.total_amount) : null,
      status: o.order_status,
      created_at: o.created_at,
    };
  });

  // Additional safeguard - ensure stats is defined
  if (!stats) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (statsLoading || ordersLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const kpis = [
    { label: 'Total Users', value: fmtInt(stats?.totalUsers), icon: <UserOutlined /> },
    { label: 'Total Orders', value: fmtInt(stats?.totalOrders), icon: <ShoppingCartOutlined /> },
    { label: 'Total Revenue', value: fmtMoney(stats?.totalRevenue), icon: <DollarOutlined /> },
    { label: 'Total Profit', value: fmtMoney(stats?.totalProfit), icon: <RiseOutlined /> },
    { label: 'Active Products', value: fmtInt(stats?.activeProducts), icon: <ShopOutlined /> },
    { label: 'Total Providers', value: fmtInt(stats?.totalProviders), icon: <TeamOutlined /> },
    { label: 'Total Customers', value: fmtInt(stats?.totalCustomers), icon: <UserOutlined /> },
    {
      label: 'Delivery Personnel',
      value: fmtInt(stats?.totalDeliveryPersonnel),
      icon: <TruckOutlined />,
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]}>
        {kpis.map((k) => (
          <Col xs={12} sm={12} md={6} lg={6} key={k.label}>
            <StatTile icon={k.icon} label={k.label} value={k.value} />
          </Col>
        ))}
      </Row>

      {/* Critical alerts + live activity — monitor at a glance */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={9}>
          <Card title="Needs Attention" style={{ height: '100%' }}>
            {alerts.length ? (
              <div className="alert-list">
                {alerts.map((a) => (
                  <button key={a.key} className="alert-row" onClick={() => navigate(a.to)}>
                    <span className="alert-ico">{a.icon}</span>
                    <span className="alert-txt">
                      <span className="alert-label">{a.label}</span>
                      {a.hint && <span className="alert-hint">{a.hint}</span>}
                    </span>
                    <span className="alert-count">{fmtInt(a.count)}</span>
                    <RightOutlined className="alert-arrow" />
                  </button>
                ))}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="All clear — nothing needs attention"
                style={{ padding: '48px 0' }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={15}>
          <Card
            title="Live Activity"
            extra={<span style={{ color: '#94a3b8', fontSize: 12 }}>Updates every minute</span>}
            style={{ height: '100%' }}
          >
            {activityFeed.length ? (
              <div className="feed-list">
                {activityFeed.map((a, i) => {
                  const color = FEED_STATUS_COLOR[a.status] || '#94a3b8';
                  return (
                    <div className="feed-item" key={`${a.id}-${i}`}>
                      <span className="feed-dot" style={{ background: color }} />
                      <div className="feed-main">
                        <div className="feed-desc">
                          <strong>{a.customer}</strong> ordered <strong>{a.what}</strong>
                          {a.provider ? (
                            <>
                              {' '}
                              from <strong>{a.provider}</strong>
                            </>
                          ) : null}
                        </div>
                        <div className="feed-meta">
                          {a.amount && <span>{a.amount}</span>}
                          {a.amount && <span className="feed-sep">·</span>}
                          <span>{dayjs(a.created_at).fromNow()}</span>
                        </div>
                      </div>
                      {a.status && (
                        <Tag color={color} style={{ marginInlineEnd: 0 }}>
                          {a.status.replace(/_/g, ' ')}
                        </Tag>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No recent activity"
                style={{ padding: '48px 0' }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts Section */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={12}>
          <Card
            title="Revenue Analytics"
            extra={
              <DatePicker.RangePicker
                picker="month"
                defaultValue={[dayjs().subtract(6, 'month'), dayjs()]}
              />
            }
          >
            {revenueData.length ? (
              <Area
                data={revenueData}
                height={280}
                xField="month"
                yField="revenue"
                shapeField="smooth"
                style={{ fill: '#6d28d9', fillOpacity: 0.12 }}
                line={{ style: { stroke: '#6d28d9', lineWidth: 2.5 } }}
                axis={{
                  x: { title: null },
                  y: {
                    title: null,
                    labelFormatter: (v) =>
                      v >= 1000 ? `Rs.${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k` : `Rs.${v}`,
                  },
                }}
                tooltip={{
                  items: [
                    { field: 'revenue', name: 'Revenue', valueFormatter: (v) => fmtMoney(v) },
                  ],
                }}
              />
            ) : (
              <Empty description="No revenue data yet" style={{ padding: '80px 0' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Order Status Distribution"
            extra={<span style={{ color: '#64748b' }}>{fmtInt(totalStatusOrders)} orders</span>}
          >
            {orderStatusData.length ? (
              <Pie
                data={orderStatusData}
                height={280}
                angleField="value"
                colorField="name"
                innerRadius={0.64}
                scale={{ color: { range: orderStatusColors } }}
                legend={{ color: { position: 'bottom' } }}
                style={{ stroke: '#fff', lineWidth: 2 }}
                label={{
                  text: 'value',
                  position: 'inside',
                  style: { fill: '#fff', fontWeight: 600, fontSize: 12 },
                }}
                tooltip={{ title: (d) => d.name, items: [{ field: 'value', name: 'Orders' }] }}
              />
            ) : (
              <Empty description="No orders yet" style={{ padding: '80px 0' }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Secondary Stats */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title="Top Products">
            {topProductsData.length ? (
              <Column
                data={topProductsData}
                height={280}
                xField="name"
                yField="sales"
                style={{ fill: '#6d28d9', radius: 4 }}
                label={{ text: 'sales', position: 'top', style: { fill: '#334155' } }}
                axis={{
                  x: {
                    title: null,
                    labelFormatter: (v) => (v && v.length > 12 ? `${v.slice(0, 12)}…` : v),
                  },
                  y: { title: null },
                }}
                tooltip={{ items: [{ field: 'sales', name: 'Units sold' }] }}
              />
            ) : (
              <Empty description="No product sales yet" style={{ padding: '80px 0' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Category Distribution"
            extra={
              <span style={{ color: '#64748b' }}>{fmtInt(totalCategoryProducts)} products</span>
            }
          >
            {categoryData.length ? (
              <Pie
                data={categoryData}
                height={280}
                angleField="count"
                colorField="category"
                innerRadius={0.64}
                scale={{ color: { range: CATEGORY_COLORS } }}
                legend={{ color: { position: 'bottom' } }}
                style={{ stroke: '#fff', lineWidth: 2 }}
                label={{ text: 'category', position: 'outside', style: { fill: '#334155' } }}
                tooltip={{
                  title: (d) => d.category,
                  items: [{ field: 'count', name: 'Products' }],
                }}
              />
            ) : (
              <Empty description="No categories yet" style={{ padding: '80px 0' }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Orders - Dedicated Row */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Card title="Recent Orders">
            <Table
              columns={orderColumns}
              dataSource={recentOrders}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
              }}
              size="small"
              scroll={{ x: true }}
            />
          </Card>
        </Col>
      </Row>

      {/* System Health */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Card
            title="System Health"
            extra={
              <Space>
                <Tag color={stats?.systemHealth?.database === 'healthy' ? 'green' : 'red'}>
                  Database: {stats?.systemHealth?.database || 'Unknown'}
                </Tag>
                <Tag color={stats?.systemHealth?.api === 'running' ? 'green' : 'red'}>
                  API: {stats?.systemHealth?.api || 'Unknown'}
                </Tag>
                <Tag color={stats?.systemHealth?.cache === 'healthy' ? 'green' : 'red'}>
                  Cache: {stats?.systemHealth?.cache || 'Unknown'}
                </Tag>
              </Space>
            }
          >
            <div style={{ marginTop: 16 }}>
              <p>
                <strong>Server Status:</strong> {stats?.systemHealth?.server || 'Unknown'}
              </p>
              <p>
                <strong>Last Updated:</strong>{' '}
                {stats?.systemHealth?.last_updated
                  ? new Date(stats.systemHealth.last_updated).toLocaleString()
                  : new Date().toLocaleString()}
              </p>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
